import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, IP_BLOCK_MESSAGE, IP_BLOCK_STYLE } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mail,
  Lock,
  LogIn,
  Loader2,
  HelpCircle,
  Eye,
  EyeOff,
  ShieldCheck,
  Users,
  Wallet,
  GraduationCap,
  Target,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Logo from './ui/Logo';
import ChangePasswordModal from './ChangePasswordModal';

const FEATURES = [
  { icon: Users, label: 'Gestão de Pessoas' },
  { icon: Wallet, label: 'Folha & DP' },
  { icon: GraduationCap, label: 'LMS & PDI' },
  { icon: Target, label: 'Avaliações 360°' },
];

const LoginPage = () => {
  const { login, loading, finalizeLogin, initializeSystem } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isPasswordExpired, setIsPasswordExpired] = useState(false);
  const [resetReason, setResetReason] = useState('first_access');
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const result = await initializeSystem();
        if (result.admin_created) {
          toast.success('Sistema inicializado com usuário administrador padrão');
        }
      } catch (error) {
        console.error('Failed to initialize system:', error);
      }
    };
    init();
  }, [initializeSystem]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    setIsLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        // Verificar se precisa trocar senha
        if (result.password_must_change || result.force_password_reset || result.password_expired) {
          setPendingUser(result.user);
          setIsPasswordExpired(result.password_expired);
          setResetReason(result.force_password_reset ? 'force_reset' : 'first_access');
          setShowPasswordModal(true);
          if (result.force_password_reset) {
            toast.info('É necessário redefinir a senha para entrar.');
          } else if (result.password_expired) {
            toast.info('Sua senha expirou. Por favor, crie uma nova senha.');
          } else {
            toast.info('Por segurança, altere sua senha no primeiro acesso.');
          }
        } else {
          toast.success('Login realizado com sucesso!');
        }
      } else if (String(result.error).includes(IP_BLOCK_MESSAGE)) {
        // Bloqueio por IP: destaque em azul para diferenciar de erro de senha
        toast.error(result.error, { style: IP_BLOCK_STYLE });
      } else {
        toast.error(result.error || 'Erro ao fazer login');
      }
    } catch (error) {
      toast.error('Erro inesperado durante o login');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordModal(false);
    if (pendingUser) {
      finalizeLogin(pendingUser);
      setPendingUser(null);
    }
    toast.success('Senha alterada! Você já pode usar o sistema.');
  };

  const handlePasswordChangeClose = () => {
    setShowPasswordModal(false);
    setPendingUser(null);
    localStorage.removeItem('temp_token');
    delete axios.defaults.headers.common['Authorization'];
    toast.error('Você precisa alterar sua senha para acessar o sistema.');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-canvas">
        <div className="w-10 h-10 border-4 border-white/20 border-t-[#00D4CB] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row brand-canvas relative overflow-hidden">
      {/* Glows */}
      <div className="brand-glow brand-float" style={{ top: '-6rem', left: '-4rem', width: '28rem', height: '28rem', background: '#2563EB' }} />
      <div className="brand-glow brand-float" style={{ bottom: '-8rem', left: '20%', width: '32rem', height: '32rem', background: '#00D4CB', animationDelay: '2s' }} />

      {/* ============ Painel esquerdo — Hero ============ */}
      <div className="relative z-10 flex flex-1 flex-col justify-between p-8 sm:p-12 lg:p-16">
        <Logo variant="full" theme="dark" showTagline iconClassName="h-10 w-auto" wordClassName="text-2xl" />

        <div className="max-w-xl py-12 lg:py-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-white/80 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#00D4CB]" />
            Plataforma de Gestão de Pessoas
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
            Inteligência
            <br />
            <span className="brand-text-gradient">para gestão de pessoas.</span>
          </h1>

          <p className="mt-6 max-w-md text-base sm:text-lg text-white/60">
            DP, folha de pagamento, LMS, PDI e avaliações em um só lugar — com a
            Maya, sua assistente de RH com inteligência artificial.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2 text-sm font-medium text-white/80 backdrop-blur-sm"
              >
                <Icon className="h-4 w-4 text-[#00D4CB]" />
                {label}
              </div>
            ))}
          </div>
        </div>

        <p className="hidden lg:block text-xs text-white/40">
          © {new Date().getFullYear()} GridProfile · Inteligência para Gestão de Pessoas
        </p>
      </div>

      {/* ============ Painel direito — Acesso ============ */}
      <div className="relative z-10 w-full lg:w-[480px] xl:w-[520px] flex items-center justify-center p-6 sm:p-10 lg:p-12 bg-[#0B1220]/40 lg:bg-[#0B1220]/60 backdrop-blur-xl lg:border-l border-white/10">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold text-white">Bem-vindo 👋</h2>
          <p className="mt-1 text-sm text-white/50">Acesse sua conta para continuar</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">
                E-mail corporativo
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="seu.email@empresa.com"
                  className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">
                Senha
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-11 pr-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Entrar no GridProfile
                  </>
                )}
              </Button>

              <div className="text-center">
                <Link
                  to="/esqueci-senha"
                  className="inline-flex items-center text-sm text-slate-500 hover:text-blue-600 transition-colors"
                >
                  <HelpCircle className="h-3.5 w-3.5 mr-1" />
                  Esqueceu sua senha?
                </Link>
              </div>
            </form>
          </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={handlePasswordChangeClose}
        onSuccess={handlePasswordChangeSuccess}
        isExpired={isPasswordExpired}
        resetReason={resetReason}
        preventClose={true}
      />
    </div>
    </div>
  );
};

export default LoginPage;
